import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import TransactionTimeline from '@/components/TransactionTimeline';
import { getImageUrl, formatPrice } from '@/components/PropertyCard';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, FileText, Upload, Calendar, MapPin, User, Shield, ShieldCheck, Eye, Check, AlertTriangle, Video, Download, Scale, ExternalLink, Printer, CheckCircle2, Search, DollarSign, FileSignature, CreditCard, PenTool, Building, Package, Truck, PartyPopper, Flag, X, Send, UserPlus } from 'lucide-react';
import VerifiedBadge from '@/components/VerifiedBadge';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TransactionDashboardPage() {
  const { id } = useParams();
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingForm, setOnboardingForm] = useState({
    full_name: '', passport_number: '', passport_url: '',
    nationality: '', country_of_residence: '', phone: '', email: '', address: ''
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [poaTemplate, setPoaTemplate] = useState(null);
  const [showPoaTemplate, setShowPoaTemplate] = useState(false);
  const [poaUploading, setPoaUploading] = useState(false);
  const [ddForm, setDdForm] = useState({ title_search: 'pending', liens_check: 'pending', zoning_check: 'pending', tax_status: 'pending', notes: '' });
  const [offerForm, setOfferForm] = useState({ amount: '', currency: 'USD', conditions: '', notes: '' });
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [counterAmount, setCounterAmount] = useState('');
  const [counterNotes, setCounterNotes] = useState('');
  const [showCounterDialog, setShowCounterDialog] = useState(false);
  const [contractUploading, setContractUploading] = useState(false);
  const [paymentUploading, setPaymentUploading] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({ tracking_number: '', courier: '', notes: '' });
  const [actionLoading, setActionLoading] = useState('');
  const [availableLawyers, setAvailableLawyers] = useState([]);
  const [showAssignLawyer, setShowAssignLawyer] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const tt = (en, es) => language === 'es' ? es : en;
  const STATUS_ES = {
    idle: 'Inactivo', requested: 'Solicitado', scheduled: 'Programado', completed: 'Completado', skipped: 'Omitido',
    profile_incomplete: 'Perfil Incompleto', profile_submitted: 'Perfil Enviado', profile_verified: 'Perfil Verificado', incomplete: 'Incompleto',
    poa_missing: 'Poder Faltante', poa_uploaded: 'Poder Subido', poa_verified: 'Poder Verificado',
    pending: 'Pendiente', in_progress: 'En Progreso', failed: 'Fallido',
    not_started: 'No Iniciado', submitted: 'Enviada', accepted: 'Aceptada', rejected: 'Rechazada', counter_offer: 'Contra Oferta',
    draft_uploaded: 'Borrador Subido', under_review: 'En Revision', changes_requested: 'Cambios Solicitados', approved: 'Aprobado',
    proof_uploaded: 'Comprobante Subido', confirmed: 'Confirmado', flagged: 'Marcado',
    registered: 'Registrado', ownership_confirmed: 'Propiedad Confirmada',
    shipment_pending: 'Envio Pendiente', in_transit: 'En Transito', delivered: 'Entregado',
    clear: 'Limpio', issue_found: 'Problema Encontrado',
    'not started': 'No Iniciado',
  };
  const ts = (status) => {
    if (!status) return '';
    if (language === 'es') return STATUS_ES[status] || status.replace(/_/g, ' ');
    return status.replace(/_/g, ' ');
  };
  const isBuyer = user?.id === tx?.buyer_id;
  const isAgent = user?.id === tx?.agent_id || user?.role === 'agent';
  const isLawyer = user?.id === tx?.lawyer_id || user?.role === 'lawyer';

  const fetchTx = () => {
    axios.get(`${API}/transactions/${id}`, { headers })
      .then(res => {
        setTx(res.data);
        if (res.data.buyer_profile?.full_name) {
          setOnboardingForm(res.data.buyer_profile);
        }
      })
      .catch(() => toast.error(tt('Transaction not found', 'Transaccion no encontrada')))
      .finally(() => setLoading(false));
  };

  const fetchPoaTemplate = async () => {
    try {
      const res = await axios.get(`${API}/transactions/${id}/poa/template`, { headers });
      setPoaTemplate(res.data);
      setShowPoaTemplate(true);
    } catch { toast.error(tt('Failed to generate template', 'Error al generar plantilla')); }
  };

  const uploadPoa = async (fileUrl, fileName) => {
    setPoaUploading(true);
    try {
      await axios.post(`${API}/transactions/${id}/documents/add`, {
        doc_type: 'poa', name: fileName || tt('Power of Attorney', 'Poder Notarial'), url: fileUrl, notes: tt('POA document', 'Documento de poder')
      }, { headers });
      await axios.put(`${API}/transactions/${id}/poa/upload`, {}, { headers });
      toast.success(tt('POA uploaded successfully!', 'Poder notarial subido!'));
      fetchTx();
    } catch { toast.error(tt('Failed to upload POA', 'Error al subir el poder')); }
    setPoaUploading(false);
  };

  const doAction = async (url, method, body, successMsg) => {
    setActionLoading(url);
    try {
      if (method === 'put') await axios.put(url, body, { headers });
      else await axios.post(url, body, { headers });
      toast.success(successMsg);
      fetchTx();
    } catch (err) { toast.error(err.response?.data?.detail || tt('Action failed', 'Accion fallida')); }
    setActionLoading('');
  };

  const submitOffer = async () => {
    if (!offerForm.amount) return;
    await doAction(`${API}/transactions/${id}/offer`, 'post', {
      amount: parseFloat(offerForm.amount), currency: offerForm.currency,
      conditions: offerForm.conditions, notes: offerForm.notes
    }, tt('Offer submitted!', 'Oferta enviada!'));
    setShowOfferDialog(false);
  };

  const respondOffer = async (action) => {
    const body = { action, notes: counterNotes };
    if (action === 'counter' && counterAmount) body.counter_amount = parseFloat(counterAmount);
    await doAction(`${API}/transactions/${id}/offer/respond`, 'put', body,
      tt(`Offer ${action}ed`, `Oferta ${action === 'accept' ? 'aceptada' : action === 'reject' ? 'rechazada' : 'contra-ofertada'}`));
    setShowCounterDialog(false);
  };

  const uploadFile = async (file, docType, docName, afterUploadAction) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      let fileUrl;
      try {
        const uploadRes = await axios.post(`${API.replace('/api', '')}/api/upload`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        fileUrl = uploadRes.data.url;
      } catch { fileUrl = `uploaded://${file.name}`; }
      await axios.post(`${API}/transactions/${id}/documents/add`, {
        doc_type: docType, name: docName || file.name, url: fileUrl, notes: ''
      }, { headers });
      if (afterUploadAction) await afterUploadAction(fileUrl);
      toast.success(tt('File uploaded!', 'Archivo subido!'));
      fetchTx();
    } catch { toast.error(tt('Upload failed', 'Error al subir archivo')); }
  };

  useEffect(() => { fetchTx(); }, [id]);

  const fetchLawyers = async () => {
    try {
      const res = await axios.get(`${API}/transactions/lawyers/available`, { headers });
      setAvailableLawyers(res.data.lawyers || []);
      setShowAssignLawyer(true);
    } catch { toast.error(tt('Failed to load lawyers', 'Error al cargar abogados')); }
  };

  const assignLawyer = async (lawyerId) => {
    try {
      await axios.put(`${API}/transactions/${id}/lawyer`, { lawyer_id: lawyerId }, { headers });
      toast.success(tt('Lawyer assigned!', 'Abogado asignado!'));
      setShowAssignLawyer(false);
      fetchTx();
    } catch (err) { toast.error(err.response?.data?.detail || tt('Failed', 'Error')); }
  };

  const [inviteMessage, setInviteMessage] = useState('');
  const inviteLawyer = async (lawyerId) => {
    try {
      await axios.post(`${API}/transactions/${id}/invite-lawyer`, { lawyer_id: lawyerId, message: inviteMessage || null }, { headers });
      toast.success(tt('Invitation sent! Lawyer has 72h to respond.', '¡Invitación enviada! El abogado tiene 72h para responder.'));
      setShowAssignLawyer(false);
      setInviteMessage('');
      fetchTx();
    } catch (err) { toast.error(err.response?.data?.detail || tt('Failed', 'Error')); }
  };

  const updateStage = async (stage, status, notes = '') => {
    try {
      await axios.put(`${API}/transactions/${id}/stage`, { stage, status, notes }, { headers });
      toast.success(tt('Status updated', 'Estado actualizado'));
      fetchTx();
    } catch (err) { toast.error(err.response?.data?.detail || tt('Failed', 'Error')); }
  };

  const submitOnboarding = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/transactions/${id}/onboarding`, onboardingForm, { headers });
      toast.success(tt('Profile submitted!', 'Perfil enviado!'));
      setShowOnboarding(false);
      fetchTx();
    } catch (err) { toast.error(err.response?.data?.detail || tt('Failed', 'Error')); }
    setSaving(false);
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-4">
      <Skeleton className="h-10 w-64" /><Skeleton className="h-64 rounded-xl" />
    </div>
  );

  if (!tx) return (
    <div className="max-w-7xl mx-auto px-4 py-16 text-center">
      <p className="text-slate-500">{tt('Transaction not found', 'Transaccion no encontrada')}</p>
    </div>
  );

  const prop = tx.property || {};
  const stages = tx.stages || {};
  const tourStatus = stages.tour?.status;
  const onboardingStatus = stages.onboarding?.status;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="transaction-dashboard">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/transactions"><Button variant="ghost" size="sm" className="gap-1"><ArrowLeft className="w-4 h-4" />{tt('Back', 'Volver')}</Button></Link>
        <div className="flex-1">
          <h1 className="font-heading text-xl md:text-2xl font-semibold">{tt('Transaction Dashboard', 'Panel de Transaccion')}</h1>
          <p className="text-xs text-slate-500 mt-0.5">ID: {tx.id.slice(0, 8)}...</p>
        </div>
        <Badge className={isBuyer ? 'bg-blue-100 text-blue-700' : isAgent ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}>
          {isBuyer ? tt('Buyer', 'Comprador') : isAgent ? tt('Agent', 'Agente') : tt('Lawyer', 'Abogado')}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Timeline Sidebar */}
        <div className="lg:col-span-1 order-2 lg:order-1">
          <TransactionTimeline stages={stages} currentStage={tx.current_stage} />
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 order-1 lg:order-2 space-y-5">
          {/* Property Card */}
          <Card data-testid="tx-property-card">
            <CardContent className="p-4 flex gap-4">
              <div className="w-24 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                <img src={getImageUrl(prop.main_photo)} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base truncate">{prop.title}</h3>
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" />{prop.city}</p>
                <p className="font-heading font-bold text-lg text-primary mt-1">{formatPrice(prop.price, prop.currency, prop.operation_type)}</p>
              </div>
              <Link to={`/property/${prop.id}`}><Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button></Link>
            </CardContent>
          </Card>

          {/* Agent/Lawyer Actions Needed Banner */}
          {(isAgent || isLawyer) && (() => {
            const actions = [];
            if (stages.tour?.status === 'requested') actions.push({ label: tt('Schedule Tour', 'Programar Tour'), stage: 'tour' });
            if (stages.onboarding?.status === 'profile_submitted') actions.push({ label: tt('Verify Buyer', 'Verificar Comprador'), stage: 'onboarding' });
            if (stages.poa?.status === 'poa_uploaded') actions.push({ label: tt('Verify POA', 'Verificar Poder'), stage: 'poa' });
            if (stages.due_diligence?.status === 'pending' || stages.due_diligence?.status === 'in_progress') actions.push({ label: tt('Complete Due Diligence', 'Completar Debida Diligencia'), stage: 'due_diligence' });
            if (stages.offer?.status === 'submitted') actions.push({ label: tt('Respond to Offer', 'Responder Oferta'), stage: 'offer' });
            if (stages.contract?.status === 'not_started' && stages.offer?.status === 'accepted') actions.push({ label: tt('Upload Contract', 'Subir Contrato'), stage: 'contract' });
            if (stages.payment?.status === 'proof_uploaded') actions.push({ label: tt('Confirm Payment', 'Confirmar Pago'), stage: 'payment' });
            if (stages.signing?.status === 'not_started' && stages.payment?.status === 'confirmed') actions.push({ label: tt('Start Signing', 'Iniciar Firma'), stage: 'signing' });
            if (stages.registry?.status === 'not_started' && stages.signing?.status === 'completed') actions.push({ label: tt('Submit Registry', 'Enviar Registro'), stage: 'registry' });
            if (actions.length === 0) return null;
            return (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4" data-testid="agent-actions-needed">
                <p className="text-xs font-semibold text-amber-700 uppercase mb-2">{tt('Actions Needed', 'Acciones Requeridas')}</p>
                <div className="flex flex-wrap gap-2">
                  {actions.map(a => (
                    <button key={a.stage} onClick={() => document.querySelector(`[data-testid="tx-${a.stage.replace('_', '-')}-section"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                      className="text-xs px-3 py-1.5 rounded-full bg-amber-200/60 text-amber-800 hover:bg-amber-300/60 font-medium transition-colors">
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Virtual Tour Section */}
          <Card data-testid="tx-tour-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Virtual Tour', 'Tour Virtual')}</h3>
                </div>
                <Badge className={tourStatus === 'completed' || tourStatus === 'skipped' ? 'bg-green-100 text-green-700' : tourStatus === 'scheduled' ? 'bg-blue-100 text-blue-700' : tourStatus === 'requested' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}>
                  {ts(tourStatus || 'idle')}
                </Badge>
              </div>
              {tx.tours?.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  {tx.tours.map(tour => (
                    <div key={tour.id} className="flex justify-between text-sm">
                      <div>
                        <p className="font-medium">{tt('Preferred', 'Preferido')}: {tour.preferred_date} {tour.preferred_time}</p>
                        {tour.scheduled_date && <p className="text-xs text-green-600">{tt('Scheduled', 'Programado')}: {tour.scheduled_date} {tour.scheduled_time}</p>}
                        {tour.notes && <p className="text-xs text-slate-500 mt-1">{tour.notes}</p>}
                        {tour.meeting_link && <a href={tour.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-primary underline">{tt('Join Meeting', 'Unirse a Reunion')}</a>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Agent actions */}
              {isAgent && tourStatus === 'requested' && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="schedule-tour-btn"
                    onClick={() => {
                      const tour = tx.tours?.[0];
                      if (tour) {
                        axios.put(`${API}/transactions/tours/${tour.id}`, {
                          status: 'scheduled',
                          scheduled_date: tour.preferred_date,
                          scheduled_time: tour.preferred_time,
                          meeting_link: 'https://zoom.us/j/meeting'
                        }, { headers }).then(() => { toast.success(tt('Tour scheduled', 'Tour programado')); fetchTx(); });
                      }
                    }}>
                    <Calendar className="w-3.5 h-3.5 mr-1" />{tt('Schedule Tour', 'Programar Tour')}
                  </Button>
                </div>
              )}
              {isAgent && tourStatus === 'scheduled' && (
                <Button size="sm" className="mt-3 bg-green-600 hover:bg-green-700 text-white" data-testid="complete-tour-btn"
                  onClick={() => {
                    const tour = tx.tours?.[0];
                    if (tour) {
                      axios.put(`${API}/transactions/tours/${tour.id}`, { status: 'completed' }, { headers })
                        .then(() => { toast.success(tt('Tour completed', 'Tour completado')); fetchTx(); });
                    }
                  }}>
                  <Check className="w-3.5 h-3.5 mr-1" />{tt('Mark Completed', 'Marcar Completado')}
                </Button>
              )}
              {isBuyer && tourStatus === 'idle' && (
                <p className="text-sm text-slate-400 mt-2">{tt('Request a virtual tour from the property detail page.', 'Solicita un tour virtual desde la pagina de la propiedad.')}</p>
              )}
            </CardContent>
          </Card>

          {/* Buyer Onboarding Section */}
          <Card data-testid="tx-onboarding-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Buyer Verification', 'Verificacion del Comprador')}</h3>
                </div>
                <Badge className={onboardingStatus === 'profile_verified' ? 'bg-green-100 text-green-700' : onboardingStatus === 'profile_submitted' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}>
                  {ts(onboardingStatus || 'incomplete')}
                </Badge>
              </div>

              {tx.buyer_profile?.full_name ? (
                <div className="bg-slate-50 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm" data-testid="buyer-profile-display">
                  <div><span className="text-slate-400 text-xs">{tt('Name', 'Nombre')}</span><p className="font-medium">{tx.buyer_profile.full_name}</p></div>
                  <div><span className="text-slate-400 text-xs">{tt('Passport', 'Pasaporte')}</span><p className="font-medium">{tx.buyer_profile.passport_number}</p></div>
                  <div><span className="text-slate-400 text-xs">{tt('Nationality', 'Nacionalidad')}</span><p className="font-medium">{tx.buyer_profile.nationality}</p></div>
                  <div><span className="text-slate-400 text-xs">{tt('Country', 'Pais')}</span><p className="font-medium">{tx.buyer_profile.country_of_residence}</p></div>
                  <div><span className="text-slate-400 text-xs">Email</span><p className="font-medium">{tx.buyer_profile.email}</p></div>
                  <div><span className="text-slate-400 text-xs">{tt('Phone', 'Telefono')}</span><p className="font-medium">{tx.buyer_profile.phone}</p></div>
                </div>
              ) : (
                <p className="text-sm text-slate-400">{tt('Buyer has not submitted their profile yet.', 'El comprador aun no ha enviado su perfil.')}</p>
              )}

              {isBuyer && onboardingStatus !== 'profile_verified' && (
                <Dialog open={showOnboarding} onOpenChange={setShowOnboarding}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="mt-3 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1" data-testid="open-onboarding-btn">
                      <Upload className="w-3.5 h-3.5" />{tx.buyer_profile?.full_name ? tt('Update Profile', 'Actualizar Perfil') : tt('Submit Documents', 'Enviar Documentos')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg" aria-describedby="onboarding-desc">
                    <DialogHeader><DialogTitle>{tt('Buyer Onboarding', 'Registro de Comprador')}</DialogTitle></DialogHeader>
                    <p id="onboarding-desc" className="text-sm text-slate-500 mb-3">{tt('Submit your personal details and passport for verification.', 'Envie sus datos personales y pasaporte para verificacion.')}</p>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">{tt('Full Name', 'Nombre Completo')}</Label><Input value={onboardingForm.full_name} onChange={e => setOnboardingForm(f => ({ ...f, full_name: e.target.value }))} className="mt-1" data-testid="onboard-name" /></div>
                        <div><Label className="text-xs">{tt('Passport #', 'N Pasaporte')}</Label><Input value={onboardingForm.passport_number} onChange={e => setOnboardingForm(f => ({ ...f, passport_number: e.target.value }))} className="mt-1" data-testid="onboard-passport" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">{tt('Nationality', 'Nacionalidad')}</Label><Input value={onboardingForm.nationality} onChange={e => setOnboardingForm(f => ({ ...f, nationality: e.target.value }))} className="mt-1" data-testid="onboard-nationality" /></div>
                        <div><Label className="text-xs">{tt('Country of Residence', 'Pais de Residencia')}</Label><Input value={onboardingForm.country_of_residence} onChange={e => setOnboardingForm(f => ({ ...f, country_of_residence: e.target.value }))} className="mt-1" data-testid="onboard-country" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label className="text-xs">Email</Label><Input value={onboardingForm.email} onChange={e => setOnboardingForm(f => ({ ...f, email: e.target.value }))} className="mt-1" data-testid="onboard-email" /></div>
                        <div><Label className="text-xs">{tt('Phone', 'Telefono')}</Label><Input value={onboardingForm.phone} onChange={e => setOnboardingForm(f => ({ ...f, phone: e.target.value }))} className="mt-1" data-testid="onboard-phone" /></div>
                      </div>
                      <div><Label className="text-xs">{tt('Address', 'Direccion')}</Label><Input value={onboardingForm.address} onChange={e => setOnboardingForm(f => ({ ...f, address: e.target.value }))} className="mt-1" data-testid="onboard-address" /></div>
                    </div>
                    <Button onClick={submitOnboarding} disabled={saving || !onboardingForm.full_name || !onboardingForm.passport_number}
                      className="w-full mt-3 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="submit-onboarding-btn">
                      {saving ? tt('Submitting...', 'Enviando...') : tt('Submit', 'Enviar')}
                    </Button>
                  </DialogContent>
                </Dialog>
              )}

              {(isAgent || isLawyer) && onboardingStatus === 'profile_submitted' && (
                <Button size="sm" className="mt-3 bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="verify-buyer-btn"
                  onClick={() => updateStage('onboarding', 'profile_verified', tt('Verified by agent', 'Verificado por agente'))}>
                  <Shield className="w-3.5 h-3.5" />{tt('Verify Buyer', 'Verificar Comprador')}
                </Button>
              )}
            </CardContent>
          </Card>


          {/* POA Section */}
          <Card data-testid="tx-poa-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Power of Attorney (POA)', 'Poder Notarial')}</h3>
                </div>
                <Badge className={
                  stages.poa?.status === 'poa_verified' ? 'bg-green-100 text-green-700' :
                  stages.poa?.status === 'poa_uploaded' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-500'
                } data-testid="poa-status-badge">
                  {ts(stages.poa?.status || 'poa_missing')}
                </Badge>
              </div>

              {/* Explanation */}
              {stages.poa?.status === 'poa_missing' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="poa-explanation">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-sm text-amber-800 mb-1">
                        {tt('Power of Attorney Required', 'Se Requiere Poder Notarial')}
                      </p>
                      <p className="text-xs text-amber-700 leading-relaxed">
                        {tt(
                          'As an international buyer, you need a Power of Attorney (POA) to authorize a representative in Venezuela to act on your behalf during the property purchase. The POA must be notarized at a Venezuelan consulate in your country.',
                          'Como comprador internacional, necesita un Poder Notarial para autorizar a un representante en Venezuela a actuar en su nombre durante la compra. El poder debe ser notarizado en un consulado venezolano en su pais.'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Verified state */}
              {stages.poa?.status === 'poa_verified' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3" data-testid="poa-verified-banner">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-sm text-green-800">{tt('POA Verified', 'Poder Verificado')}</p>
                    {stages.poa?.notes && <p className="text-xs text-green-600">{stages.poa.notes}</p>}
                  </div>
                </div>
              )}

              {/* Uploaded state */}
              {stages.poa?.status === 'poa_uploaded' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3" data-testid="poa-uploaded-banner">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-semibold text-sm text-blue-800">{tt('POA Uploaded — Pending Verification', 'Poder Subido — Pendiente de Verificacion')}</p>
                    <p className="text-xs text-blue-600">{tt('Waiting for agent or lawyer to verify your document.', 'Esperando verificacion por el agente o abogado.')}</p>
                  </div>
                </div>
              )}

              {/* POA Documents */}
              {tx.documents?.filter(d => d.doc_type === 'poa').length > 0 && (
                <div className="mt-3 space-y-2">
                  {tx.documents.filter(d => d.doc_type === 'poa').map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        <div>
                          <p className="font-medium">{doc.name}</p>
                          <p className="text-xs text-slate-400">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {doc.url && <a href={doc.url} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><Download className="w-3.5 h-3.5" /></Button></a>}
                    </div>
                  ))}
                </div>
              )}

              {/* Buyer Actions */}
              {isBuyer && stages.poa?.status !== 'poa_verified' && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={fetchPoaTemplate} data-testid="generate-poa-btn">
                    <Printer className="w-3.5 h-3.5" />{tt('Generate POA Template', 'Generar Plantilla de Poder')}
                  </Button>
                  <label>
                    <Button size="sm" className="gap-1.5 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] cursor-pointer" asChild data-testid="upload-poa-btn">
                      <span>
                        <Upload className="w-3.5 h-3.5" />{poaUploading ? tt('Uploading...', 'Subiendo...') : tt('Upload POA', 'Subir Poder')}
                      </span>
                    </Button>
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setPoaUploading(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const uploadRes = await axios.post(`${API.replace('/api', '')}/api/upload`, formData, {
                          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
                        });
                        await uploadPoa(uploadRes.data.url, file.name);
                      } catch {
                        await uploadPoa(`uploaded://${file.name}`, file.name);
                      }
                    }} />
                  </label>
                </div>
              )}

              {/* Agent/Lawyer verify action */}
              {(isAgent || isLawyer) && stages.poa?.status === 'poa_uploaded' && (
                <Button size="sm" className="mt-3 bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="verify-poa-btn"
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/transactions/${id}/poa/verify`, {}, { headers });
                      toast.success(tt('POA verified', 'Poder verificado'));
                      fetchTx();
                    } catch { toast.error(tt('Failed', 'Error')); }
                  }}>
                  <Shield className="w-3.5 h-3.5" />{tt('Verify POA', 'Verificar Poder')}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* POA Template Modal */}
          <Dialog open={showPoaTemplate} onOpenChange={setShowPoaTemplate}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="poa-template-desc">
              <DialogHeader><DialogTitle>{tt('Power of Attorney Template', 'Plantilla de Poder Notarial')}</DialogTitle></DialogHeader>
              <p id="poa-template-desc" className="text-sm text-slate-500">{tt('Review and print this template. Fill in missing fields before notarization.', 'Revise e imprima esta plantilla. Complete los campos faltantes antes de notarizar.')}</p>
              {poaTemplate && (
                <div className="space-y-5 mt-2" data-testid="poa-template-content">
                  <div className="text-center border-b pb-4">
                    <h2 className="font-heading font-bold text-base">{poaTemplate.title}</h2>
                    <p className="text-xs text-slate-400 mt-1">{tt('Generated', 'Generado')}: {new Date(poaTemplate.generated_at).toLocaleDateString()}</p>
                  </div>
                  {poaTemplate.sections?.map((section, i) => (
                    <div key={i} className="border rounded-lg p-4">
                      <h3 className="font-semibold text-sm text-primary mb-2">{section.heading}</h3>
                      {section.fields && (
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(section.fields).map(([key, val]) => (
                            <div key={key}>
                              <span className="text-[10px] text-slate-400 uppercase">{key.replace(/_/g, ' ')}</span>
                              <p className={`text-sm font-medium ${val.startsWith('[') ? 'text-red-500 italic' : 'text-slate-700'}`}>{val}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {section.content && (
                        <ul className="space-y-1.5">
                          {section.content.map((item, j) => (
                            <li key={j} className="text-sm text-slate-600 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                  {poaTemplate.consulate_instructions && (
                    <div className="bg-primary/5 border border-primary/20 rounded-xl p-5" data-testid="consulate-instructions">
                      <h3 className="font-heading font-semibold text-sm mb-3">{poaTemplate.consulate_instructions.title}</h3>
                      <div className="space-y-3">
                        {poaTemplate.consulate_instructions.steps.map(step => (
                          <div key={step.step} className="flex gap-3">
                            <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{step.step}</div>
                            <p className="text-sm text-slate-700">{language === 'es' ? step.es : step.en}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 p-3 bg-white rounded-lg border border-primary/10">
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {language === 'es' ? poaTemplate.consulate_instructions.note_es : poaTemplate.consulate_instructions.note_en}
                        </p>
                        <a href={poaTemplate.consulate_instructions.consulate_finder_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary font-medium mt-2 hover:underline">
                          <ExternalLink className="w-3 h-3" />{tt('Find Venezuelan Consulate', 'Buscar Consulado Venezolano')}
                        </a>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1 gap-1" onClick={() => window.print()} data-testid="print-poa-btn">
                      <Printer className="w-4 h-4" />{tt('Print Template', 'Imprimir Plantilla')}
                    </Button>
                    <Button className="flex-1 gap-1 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" onClick={() => setShowPoaTemplate(false)}>
                      {tt('Close', 'Cerrar')}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* ===================== DUE DILIGENCE SECTION ===================== */}
          <Card data-testid="tx-due-diligence-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Due Diligence', 'Debida Diligencia')}</h3>
                </div>
                <Badge className={
                  stages.due_diligence?.status === 'completed' ? 'bg-green-100 text-green-700' :
                  stages.due_diligence?.status === 'failed' ? 'bg-red-100 text-red-700' :
                  stages.due_diligence?.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-500'
                } data-testid="dd-status-badge">
                  {ts(stages.due_diligence?.status || 'pending')}
                </Badge>
              </div>

              {stages.due_diligence?.metadata?.pre_cleared && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3 mb-3" data-testid="dd-pre-cleared-banner">
                  <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-emerald-800">
                      {tt('Pre-cleared by WakaCasa Property Verification', 'Pre-aprobada por la Verificación de Propiedad de WakaCasa')}
                    </p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      {tt(
                        'This property is WakaCasa-Verified. All 4 checks below were auto-marked as clear; the lawyer can override any item if issues arise.',
                        'Esta propiedad está Verificada por WakaCasa. Las 4 verificaciones se marcaron como aprobadas automáticamente; el abogado puede modificar cualquier ítem si surgen problemas.'
                      )}
                    </p>
                  </div>
                </div>
              )}

              {stages.due_diligence?.status === 'completed' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-sm text-green-800">{tt('Due Diligence Passed', 'Debida Diligencia Aprobada')}</p>
                    <p className="text-xs text-green-600">{tt('All checks cleared. Property is ready for offer.', 'Todas las verificaciones aprobadas. Propiedad lista para oferta.')}</p>
                  </div>
                </div>
              )}
              {stages.due_diligence?.status === 'failed' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-semibold text-sm text-red-800">{tt('Issues Found', 'Problemas Encontrados')}</p>
                    {stages.due_diligence?.notes && <p className="text-xs text-red-600">{stages.due_diligence.notes}</p>}
                  </div>
                </div>
              )}

              {/* DD Checklist - shown for all, editable by agent/lawyer */}
              {(() => {
                const dd = stages.due_diligence?.metadata || {};
                const checks = [
                  { key: 'title_search', en: 'Title Search', es: 'Busqueda de Titulo' },
                  { key: 'liens_check', en: 'Liens & Encumbrances', es: 'Gravamenes y Cargas' },
                  { key: 'zoning_check', en: 'Zoning Compliance', es: 'Cumplimiento Zonificacion' },
                  { key: 'tax_status', en: 'Tax Status', es: 'Estado Fiscal' },
                ];
                return (
                  <div className="mt-3 space-y-2">
                    {checks.map(ck => {
                      const val = dd[ck.key] || 'pending';
                      return (
                        <div key={ck.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`dd-${ck.key}`}>
                          <span className="text-sm font-medium">{tt(ck.en, ck.es)}</span>
                          {(isAgent || isLawyer) && stages.due_diligence?.status !== 'completed' ? (
                            <select className="text-xs border rounded px-2 py-1" value={ddForm[ck.key] || val}
                              data-testid={`dd-select-${ck.key}`}
                              onChange={e => setDdForm(f => ({ ...f, [ck.key]: e.target.value }))}>
                              <option value="pending">{tt('Pending', 'Pendiente')}</option>
                              <option value="clear">{tt('Clear', 'Limpio')}</option>
                              <option value="issue_found">{tt('Issue Found', 'Problema Encontrado')}</option>
                            </select>
                          ) : (
                            <Badge className={val === 'clear' ? 'bg-green-100 text-green-700' : val === 'issue_found' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}>
                              {ts(val)}
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                    {(isAgent || isLawyer) && stages.due_diligence?.status !== 'completed' && (
                      <Button size="sm" className="mt-2 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1" data-testid="save-dd-btn"
                        disabled={actionLoading !== ''}
                        onClick={() => doAction(`${API}/transactions/${id}/due-diligence`, 'put', ddForm, tt('Due diligence updated', 'Debida diligencia actualizada'))}>
                        <Check className="w-3.5 h-3.5" />{tt('Save Checks', 'Guardar Verificaciones')}
                      </Button>
                    )}
                  </div>
                );
              })()}

              {isBuyer && stages.due_diligence?.status === 'pending' && (
                <p className="text-sm text-slate-400 mt-3">{tt('Your agent or lawyer will conduct property checks.', 'Su agente o abogado realizara las verificaciones de la propiedad.')}</p>
              )}
            </CardContent>
          </Card>

          {/* ===================== OFFER SECTION ===================== */}
          <Card data-testid="tx-offer-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Offer', 'Oferta')}</h3>
                </div>
                <Badge className={
                  stages.offer?.status === 'accepted' ? 'bg-green-100 text-green-700' :
                  stages.offer?.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  stages.offer?.status === 'submitted' || stages.offer?.status === 'counter_offer' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-500'
                } data-testid="offer-status-badge">
                  {ts(stages.offer?.status || 'not_started')}
                </Badge>
              </div>

              {stages.offer?.status === 'accepted' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-sm text-green-800">{tt('Offer Accepted', 'Oferta Aceptada')}</p>
                    <p className="text-xs text-green-600">${stages.offer?.metadata?.amount?.toLocaleString()} {stages.offer?.metadata?.currency}</p>
                  </div>
                </div>
              )}
              {stages.offer?.status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <X className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-semibold text-sm text-red-800">{tt('Offer Rejected', 'Oferta Rechazada')}</p>
                    {stages.offer?.notes && <p className="text-xs text-red-600">{stages.offer.notes}</p>}
                  </div>
                </div>
              )}

              {/* Current offer details */}
              {stages.offer?.metadata?.amount && stages.offer?.status !== 'not_started' && (
                <div className="bg-slate-50 rounded-lg p-4 mt-2 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{tt('Offer Amount', 'Monto de Oferta')}</span>
                    <span className="font-bold">${stages.offer.metadata.amount?.toLocaleString()} {stages.offer.metadata.currency}</span>
                  </div>
                  {stages.offer.metadata.counter_amount && (
                    <div className="flex justify-between text-sm">
                      <span className="text-amber-600">{tt('Counter Offer', 'Contra Oferta')}</span>
                      <span className="font-bold text-amber-700">${stages.offer.metadata.counter_amount?.toLocaleString()}</span>
                    </div>
                  )}
                  {stages.offer.metadata.conditions && (
                    <div className="text-xs text-slate-500 border-t pt-2 mt-2">{stages.offer.metadata.conditions}</div>
                  )}
                </div>
              )}

              {/* Buyer: submit offer */}
              {isBuyer && (stages.offer?.status === 'not_started' || stages.offer?.status === 'counter_offer' || stages.offer?.status === 'rejected') && (
                <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="mt-3 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1" data-testid="submit-offer-btn">
                      <Send className="w-3.5 h-3.5" />{stages.offer?.status === 'counter_offer' ? tt('Revise Offer', 'Revisar Oferta') : tt('Make an Offer', 'Hacer Oferta')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md" aria-describedby="offer-desc">
                    <DialogHeader><DialogTitle>{tt('Submit Offer', 'Enviar Oferta')}</DialogTitle></DialogHeader>
                    <p id="offer-desc" className="text-sm text-slate-500">{tt('Enter your offer details for this property.', 'Ingrese los detalles de su oferta para esta propiedad.')}</p>
                    <div className="space-y-3 mt-2">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2">
                          <Label className="text-xs">{tt('Amount', 'Monto')}</Label>
                          <Input type="number" value={offerForm.amount} onChange={e => setOfferForm(f => ({ ...f, amount: e.target.value }))} placeholder="250000" className="mt-1" data-testid="offer-amount" />
                        </div>
                        <div>
                          <Label className="text-xs">{tt('Currency', 'Moneda')}</Label>
                          <select className="w-full border rounded-md px-2 py-2 mt-1 text-sm" value={offerForm.currency} onChange={e => setOfferForm(f => ({ ...f, currency: e.target.value }))}>
                            <option value="USD">USD</option><option value="EUR">EUR</option><option value="VES">VES</option>
                          </select>
                        </div>
                      </div>
                      <div><Label className="text-xs">{tt('Conditions', 'Condiciones')}</Label><Textarea value={offerForm.conditions} onChange={e => setOfferForm(f => ({ ...f, conditions: e.target.value }))} className="mt-1" rows={2} data-testid="offer-conditions" /></div>
                      <div><Label className="text-xs">{tt('Notes', 'Notas')}</Label><Textarea value={offerForm.notes} onChange={e => setOfferForm(f => ({ ...f, notes: e.target.value }))} className="mt-1" rows={2} data-testid="offer-notes" /></div>
                      <Button onClick={submitOffer} disabled={!offerForm.amount || actionLoading !== ''}
                        className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="confirm-offer-btn">
                        {tt('Submit Offer', 'Enviar Oferta')}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
              {isBuyer && stages.offer?.status === 'submitted' && (
                <p className="text-sm text-amber-600 mt-3">{tt('Waiting for agent response...', 'Esperando respuesta del agente...')}</p>
              )}

              {/* Agent: respond to offer */}
              {isAgent && stages.offer?.status === 'submitted' && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="accept-offer-btn"
                    onClick={() => respondOffer('accept')}>
                    <Check className="w-3.5 h-3.5" />{tt('Accept', 'Aceptar')}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-offer-btn"
                    onClick={() => respondOffer('reject')}>
                    <X className="w-3.5 h-3.5" />{tt('Reject', 'Rechazar')}
                  </Button>
                  <Dialog open={showCounterDialog} onOpenChange={setShowCounterDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="gap-1" data-testid="counter-offer-btn">
                        <DollarSign className="w-3.5 h-3.5" />{tt('Counter', 'Contra Oferta')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-sm" aria-describedby="counter-desc">
                      <DialogHeader><DialogTitle>{tt('Counter Offer', 'Contra Oferta')}</DialogTitle></DialogHeader>
                      <p id="counter-desc" className="text-sm text-slate-500">{tt('Propose a different amount.', 'Proponga un monto diferente.')}</p>
                      <div className="space-y-3 mt-2">
                        <div><Label className="text-xs">{tt('Counter Amount', 'Monto Contra')}</Label><Input type="number" value={counterAmount} onChange={e => setCounterAmount(e.target.value)} className="mt-1" data-testid="counter-amount" /></div>
                        <div><Label className="text-xs">{tt('Notes', 'Notas')}</Label><Textarea value={counterNotes} onChange={e => setCounterNotes(e.target.value)} className="mt-1" rows={2} data-testid="counter-notes" /></div>
                        <Button onClick={() => respondOffer('counter')} disabled={!counterAmount}
                          className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="confirm-counter-btn">
                          {tt('Send Counter Offer', 'Enviar Contra Oferta')}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ===================== CONTRACT SECTION ===================== */}
          <Card data-testid="tx-contract-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileSignature className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Contract', 'Contrato')}</h3>
                </div>
                <Badge className={
                  stages.contract?.status === 'approved' ? 'bg-green-100 text-green-700' :
                  stages.contract?.status === 'changes_requested' ? 'bg-red-100 text-red-700' :
                  stages.contract?.status === 'draft_uploaded' || stages.contract?.status === 'under_review' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-500'
                } data-testid="contract-status-badge">
                  {ts(stages.contract?.status || 'not_started')}
                </Badge>
              </div>

              {stages.contract?.status === 'approved' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <p className="font-semibold text-sm text-green-800">{tt('Contract Approved', 'Contrato Aprobado')}</p>
                </div>
              )}
              {stages.contract?.status === 'changes_requested' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-semibold text-sm text-amber-800">{tt('Changes Requested', 'Cambios Solicitados')}</p>
                    {stages.contract?.notes && <p className="text-xs text-amber-600">{stages.contract.notes}</p>}
                  </div>
                </div>
              )}

              {/* Contract documents */}
              {tx.documents?.filter(d => d.doc_type === 'contract').length > 0 && (
                <div className="mt-3 space-y-2">
                  {tx.documents.filter(d => d.doc_type === 'contract').map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        <div>
                          <p className="font-medium">{doc.name}</p>
                          <p className="text-xs text-slate-400">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {doc.url && <a href={doc.url} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><Download className="w-3.5 h-3.5" /></Button></a>}
                    </div>
                  ))}
                </div>
              )}

              {/* Agent/Lawyer: upload contract */}
              {(isAgent || isLawyer) && stages.contract?.status !== 'approved' && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <label>
                    <Button size="sm" className="gap-1.5 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] cursor-pointer" asChild data-testid="upload-contract-btn">
                      <span><Upload className="w-3.5 h-3.5" />{contractUploading ? tt('Uploading...', 'Subiendo...') : tt('Upload Contract', 'Subir Contrato')}</span>
                    </Button>
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setContractUploading(true);
                      await uploadFile(file, 'contract', tt('Purchase Contract', 'Contrato de Compra'), async (url) => {
                        await axios.put(`${API}/transactions/${id}/contract`, { action: 'upload', url, notes: '' }, { headers });
                      });
                      setContractUploading(false);
                    }} />
                  </label>
                </div>
              )}

              {/* Buyer/Lawyer: review actions */}
              {(isBuyer || isLawyer) && stages.contract?.status === 'draft_uploaded' && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="approve-contract-btn"
                    onClick={() => doAction(`${API}/transactions/${id}/contract`, 'put', { action: 'approve', notes: '' }, tt('Contract approved', 'Contrato aprobado'))}>
                    <Check className="w-3.5 h-3.5" />{tt('Approve', 'Aprobar')}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1 text-amber-600 border-amber-200" data-testid="request-changes-btn"
                    onClick={() => doAction(`${API}/transactions/${id}/contract`, 'put', { action: 'request_changes', notes: tt('Changes needed', 'Cambios necesarios') }, tt('Changes requested', 'Cambios solicitados'))}>
                    <AlertTriangle className="w-3.5 h-3.5" />{tt('Request Changes', 'Solicitar Cambios')}
                  </Button>
                </div>
              )}

              {isBuyer && stages.contract?.status === 'not_started' && (
                <p className="text-sm text-slate-400 mt-2">{tt('Contract will be uploaded by the agent or lawyer.', 'El contrato sera subido por el agente o abogado.')}</p>
              )}
            </CardContent>
          </Card>

          {/* ===================== PAYMENT SECTION ===================== */}
          <Card data-testid="tx-payment-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Payment', 'Pago')}</h3>
                </div>
                <Badge className={
                  stages.payment?.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                  stages.payment?.status === 'flagged' ? 'bg-red-100 text-red-700' :
                  stages.payment?.status === 'proof_uploaded' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-500'
                } data-testid="payment-status-badge">
                  {ts(stages.payment?.status || 'pending')}
                </Badge>
              </div>

              {stages.payment?.status === 'confirmed' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <p className="font-semibold text-sm text-green-800">{tt('Payment Confirmed', 'Pago Confirmado')}</p>
                </div>
              )}
              {stages.payment?.status === 'flagged' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <Flag className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-semibold text-sm text-red-800">{tt('Payment Flagged', 'Pago Marcado')}</p>
                    {stages.payment?.notes && <p className="text-xs text-red-600">{stages.payment.notes}</p>}
                  </div>
                </div>
              )}

              {/* Payment documents */}
              {tx.documents?.filter(d => d.doc_type === 'payment_proof').length > 0 && (
                <div className="mt-3 space-y-2">
                  {tx.documents.filter(d => d.doc_type === 'payment_proof').map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        <div>
                          <p className="font-medium">{doc.name}</p>
                          <p className="text-xs text-slate-400">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {doc.url && <a href={doc.url} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><Download className="w-3.5 h-3.5" /></Button></a>}
                    </div>
                  ))}
                </div>
              )}

              {/* Buyer: upload proof */}
              {isBuyer && stages.payment?.status !== 'confirmed' && (
                <div className="mt-3">
                  <div className="bg-slate-50 rounded-lg p-4 mb-3">
                    <p className="text-xs font-semibold text-slate-600 mb-1">{tt('Payment Instructions', 'Instrucciones de Pago')}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{tt(
                      'Transfer the agreed amount to the escrow account provided by your lawyer. Upload proof of payment (bank receipt/wire transfer confirmation) below.',
                      'Transfiera el monto acordado a la cuenta de deposito en garantia proporcionada por su abogado. Suba el comprobante de pago abajo.'
                    )}</p>
                  </div>
                  <label>
                    <Button size="sm" className="gap-1.5 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] cursor-pointer" asChild data-testid="upload-payment-btn">
                      <span><Upload className="w-3.5 h-3.5" />{paymentUploading ? tt('Uploading...', 'Subiendo...') : tt('Upload Payment Proof', 'Subir Comprobante')}</span>
                    </Button>
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setPaymentUploading(true);
                      await uploadFile(file, 'payment_proof', 'Payment Proof', async (url) => {
                        await axios.put(`${API}/transactions/${id}/payment`, { action: 'upload_proof', url, notes: '' }, { headers });
                      });
                      setPaymentUploading(false);
                    }} />
                  </label>
                </div>
              )}

              {/* Agent/Lawyer: confirm/flag */}
              {(isAgent || isLawyer) && stages.payment?.status === 'proof_uploaded' && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="confirm-payment-btn"
                    onClick={() => doAction(`${API}/transactions/${id}/payment`, 'put', { action: 'confirm', notes: '' }, tt('Payment confirmed', 'Pago confirmado'))}>
                    <Check className="w-3.5 h-3.5" />{tt('Confirm Payment', 'Confirmar Pago')}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-200" data-testid="flag-payment-btn"
                    onClick={() => doAction(`${API}/transactions/${id}/payment`, 'put', { action: 'flag', notes: tt('Needs review', 'Necesita revision') }, tt('Payment flagged', 'Pago marcado'))}>
                    <Flag className="w-3.5 h-3.5" />{tt('Flag Issue', 'Marcar Problema')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ===================== SIGNING SECTION ===================== */}
          <Card data-testid="tx-signing-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <PenTool className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Signing', 'Firma')}</h3>
                </div>
                <Badge className={
                  stages.signing?.status === 'completed' ? 'bg-green-100 text-green-700' :
                  stages.signing?.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-500'
                } data-testid="signing-status-badge">
                  {ts(stages.signing?.status || 'not_started')}
                </Badge>
              </div>

              {stages.signing?.status === 'completed' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <p className="font-semibold text-sm text-green-800">{tt('All parties have signed', 'Todas las partes han firmado')}</p>
                </div>
              )}
              {stages.signing?.status === 'in_progress' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
                  <PenTool className="w-5 h-5 text-blue-600" />
                  <p className="font-semibold text-sm text-blue-800">{tt('Signing in progress', 'Firma en progreso')}</p>
                </div>
              )}

              {(isAgent || isLawyer) && stages.signing?.status !== 'completed' && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {stages.signing?.status === 'not_started' && (
                    <Button size="sm" className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1" data-testid="start-signing-btn"
                      onClick={() => doAction(`${API}/transactions/${id}/signing`, 'put', { action: 'start', notes: '' }, tt('Signing started', 'Firma iniciada'))}>
                      <PenTool className="w-3.5 h-3.5" />{tt('Start Signing Process', 'Iniciar Proceso de Firma')}
                    </Button>
                  )}
                  {stages.signing?.status === 'in_progress' && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="complete-signing-btn"
                      onClick={() => doAction(`${API}/transactions/${id}/signing`, 'put', { action: 'complete', notes: '' }, tt('Signing completed', 'Firma completada'))}>
                      <Check className="w-3.5 h-3.5" />{tt('Mark Signing Complete', 'Marcar Firma Completa')}
                    </Button>
                  )}
                </div>
              )}

              {isBuyer && stages.signing?.status === 'not_started' && (
                <p className="text-sm text-slate-400 mt-2">{tt('The signing process will be coordinated by the agent.', 'El proceso de firma sera coordinado por el agente.')}</p>
              )}
            </CardContent>
          </Card>

          {/* ===================== REGISTRY SECTION ===================== */}
          <Card data-testid="tx-registry-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Building className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Registry', 'Registro')}</h3>
                </div>
                <Badge className={
                  stages.registry?.status === 'registered' ? 'bg-green-100 text-green-700' :
                  stages.registry?.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-500'
                } data-testid="registry-status-badge">
                  {ts(stages.registry?.status || 'not_started')}
                </Badge>
              </div>

              {stages.registry?.status === 'registered' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-sm text-green-800">{tt('Property Registered', 'Propiedad Registrada')}</p>
                      {stages.registry?.metadata?.registry_number && (
                        <p className="text-xs text-green-600 mt-0.5">{tt('Registry #', 'Registro #')}: {stages.registry.metadata.registry_number}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {stages.registry?.status === 'submitted' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
                  <Building className="w-5 h-5 text-blue-600" />
                  <p className="font-semibold text-sm text-blue-800">{tt('Submitted to Registry Office', 'Enviado a Oficina de Registro')}</p>
                </div>
              )}

              {(isAgent || isLawyer) && stages.registry?.status !== 'registered' && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {stages.registry?.status === 'not_started' && (
                    <Button size="sm" className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1" data-testid="submit-registry-btn"
                      onClick={() => doAction(`${API}/transactions/${id}/registry`, 'put', { action: 'submit', notes: '' }, tt('Submitted to registry', 'Enviado al registro'))}>
                      <Send className="w-3.5 h-3.5" />{tt('Submit to Registry', 'Enviar al Registro')}
                    </Button>
                  )}
                  {stages.registry?.status === 'submitted' && (
                    <div className="flex gap-2 items-end">
                      <div>
                        <Label className="text-xs">{tt('Registry Number', 'Numero de Registro')}</Label>
                        <Input placeholder="REG-2026-XXXXX" className="mt-1 w-48" data-testid="registry-number-input"
                          onChange={e => setDeliveryForm(f => ({ ...f, registryNum: e.target.value }))} />
                      </div>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="confirm-registry-btn"
                        onClick={() => doAction(`${API}/transactions/${id}/registry`, 'put', { action: 'register', registry_number: deliveryForm.registryNum || '', notes: '' }, tt('Property registered', 'Propiedad registrada'))}>
                        <Check className="w-3.5 h-3.5" />{tt('Confirm Registration', 'Confirmar Registro')}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {isBuyer && stages.registry?.status === 'not_started' && (
                <p className="text-sm text-slate-400 mt-2">{tt('Registration will be handled by the lawyer.', 'El registro sera gestionado por el abogado.')}</p>
              )}
            </CardContent>
          </Card>

          {/* ===================== COMPLETE (OWNERSHIP) SECTION ===================== */}
          <Card data-testid="tx-complete-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <PartyPopper className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Ownership Confirmation', 'Confirmacion de Propiedad')}</h3>
                </div>
                <Badge className={
                  stages.complete?.status === 'ownership_confirmed' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                } data-testid="complete-status-badge">
                  {ts(stages.complete?.status || 'pending')}
                </Badge>
              </div>

              {stages.complete?.status === 'ownership_confirmed' ? (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 text-center">
                  <PartyPopper className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="font-heading font-bold text-lg text-green-800">{tt('Congratulations!', 'Felicidades!')}</p>
                  <p className="text-sm text-green-600 mt-1">{tt('Ownership has been confirmed. You are now the owner!', 'La propiedad ha sido confirmada. Ahora eres el propietario!')}</p>
                </div>
              ) : (
                <>
                  {(isAgent || isLawyer) && (
                    <Button size="sm" className="mt-1 bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="confirm-ownership-btn"
                      onClick={() => doAction(`${API}/transactions/${id}/complete`, 'put', { notes: '' }, tt('Ownership confirmed!', 'Propiedad confirmada!'))}>
                      <CheckCircle2 className="w-3.5 h-3.5" />{tt('Confirm Ownership Transfer', 'Confirmar Transferencia de Propiedad')}
                    </Button>
                  )}
                  {isBuyer && <p className="text-sm text-slate-400">{tt('Ownership will be confirmed once all steps are complete.', 'La propiedad se confirmara cuando todos los pasos esten completos.')}</p>}
                </>
              )}
            </CardContent>
          </Card>

          {/* ===================== DELIVERY SECTION ===================== */}
          <Card data-testid="tx-delivery-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-primary" />
                  <h3 className="font-heading font-semibold">{tt('Document Delivery', 'Entrega de Documentos')}</h3>
                </div>
                <Badge className={
                  stages.delivery?.status === 'delivered' ? 'bg-green-100 text-green-700' :
                  stages.delivery?.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                  stages.delivery?.status === 'shipment_pending' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-500'
                } data-testid="delivery-status-badge">
                  {ts(stages.delivery?.status || 'not_started')}
                </Badge>
              </div>

              {stages.delivery?.status === 'delivered' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                  <Package className="w-5 h-5 text-green-600" />
                  <p className="font-semibold text-sm text-green-800">{tt('Documents Delivered', 'Documentos Entregados')}</p>
                </div>
              )}

              {stages.delivery?.metadata?.tracking_number && (
                <div className="bg-slate-50 rounded-lg p-3 mt-2 text-sm space-y-1">
                  {stages.delivery.metadata.courier && (
                    <div className="flex justify-between"><span className="text-slate-500">{tt('Courier', 'Mensajeria')}</span><span className="font-medium">{stages.delivery.metadata.courier}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-slate-500">{tt('Tracking #', 'Seguimiento #')}</span><span className="font-medium">{stages.delivery.metadata.tracking_number}</span></div>
                </div>
              )}

              {(isAgent || isLawyer) && stages.delivery?.status !== 'delivered' && (
                <div className="mt-3 space-y-3">
                  {stages.delivery?.status === 'not_started' && (
                    <div className="flex flex-wrap gap-2 items-end">
                      <div>
                        <Label className="text-xs">{tt('Courier', 'Mensajeria')}</Label>
                        <Input value={deliveryForm.courier} onChange={e => setDeliveryForm(f => ({ ...f, courier: e.target.value }))} placeholder="DHL, FedEx..." className="mt-1 w-36" data-testid="delivery-courier" />
                      </div>
                      <div>
                        <Label className="text-xs">{tt('Tracking #', 'Seguimiento #')}</Label>
                        <Input value={deliveryForm.tracking_number} onChange={e => setDeliveryForm(f => ({ ...f, tracking_number: e.target.value }))} placeholder="1Z999..." className="mt-1 w-44" data-testid="delivery-tracking" />
                      </div>
                      <Button size="sm" className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1" data-testid="start-shipment-btn"
                        onClick={() => doAction(`${API}/transactions/${id}/delivery`, 'put', { action: 'start_shipment', ...deliveryForm }, tt('Shipment started', 'Envio iniciado'))}>
                        <Package className="w-3.5 h-3.5" />{tt('Start Shipment', 'Iniciar Envio')}
                      </Button>
                    </div>
                  )}
                  {stages.delivery?.status === 'shipment_pending' && (
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1" data-testid="mark-transit-btn"
                      onClick={() => doAction(`${API}/transactions/${id}/delivery`, 'put', { action: 'in_transit', notes: '' }, tt('Marked in transit', 'Marcado en transito'))}>
                      <Truck className="w-3.5 h-3.5" />{tt('Mark In Transit', 'Marcar En Transito')}
                    </Button>
                  )}
                  {stages.delivery?.status === 'in_transit' && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="mark-delivered-btn"
                      onClick={() => doAction(`${API}/transactions/${id}/delivery`, 'put', { action: 'delivered', notes: '' }, tt('Marked as delivered', 'Marcado como entregado'))}>
                      <Check className="w-3.5 h-3.5" />{tt('Mark Delivered', 'Marcar Entregado')}
                    </Button>
                  )}
                </div>
              )}

              {isBuyer && stages.delivery?.status === 'not_started' && (
                <p className="text-sm text-slate-400 mt-2">{tt('Original documents will be shipped to your address after registration.', 'Los documentos originales seran enviados a su direccion despues del registro.')}</p>
              )}
            </CardContent>
          </Card>

          {/* Documents Section */}
          <Card data-testid="tx-documents-section">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-primary" />
                <h3 className="font-heading font-semibold">{tt('All Documents', 'Todos los Documentos')}</h3>
              </div>
              {tx.documents?.length > 0 ? (
                <div className="space-y-2">
                  {tx.documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm" data-testid={`doc-${doc.id}`}>
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-xs text-slate-400">{language === 'es' ? ({ passport: 'pasaporte', poa: 'poder', contract: 'contrato', payment_proof: 'comprobante', title: 'titulo', other: 'otro' }[doc.doc_type] || doc.doc_type) : doc.doc_type} - {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                      </div>
                      {doc.url && <a href={doc.url} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><Download className="w-3.5 h-3.5" /></Button></a>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">{tt('No documents uploaded yet.', 'No se han subido documentos aun.')}</p>
              )}
            </CardContent>
          </Card>

          {/* Participants */}
          <Card data-testid="tx-participants">
            <CardContent className="p-5">
              <h3 className="font-heading font-semibold mb-3">{tt('Participants', 'Participantes')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100">
                  <p className="text-[10px] text-blue-500 font-semibold uppercase">{tt('Buyer', 'Comprador')}</p>
                  <p className="font-medium text-sm mt-1">{tx.buyer?.name || '-'}</p>
                  <p className="text-xs text-slate-500">{tx.buyer?.email}</p>
                </div>
                <div className="bg-green-50/50 rounded-lg p-3 border border-green-100">
                  <p className="text-[10px] text-green-500 font-semibold uppercase">{tt('Agent', 'Agente')}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <p className="font-medium text-sm">{tx.agent?.name || '-'}</p>
                    {tx.agent?.verified && <VerifiedBadge role="agent" size="xs" />}
                  </div>
                  <p className="text-xs text-slate-500">{tx.agent?.email}</p>
                </div>
                <div className="bg-purple-50/50 rounded-lg p-3 border border-purple-100">
                  <p className="text-[10px] text-purple-500 font-semibold uppercase">{tt('Lawyer', 'Abogado')}</p>
                  {tx.lawyer ? (
                    <>
                      <div className="flex items-center gap-1.5 mt-1">
                        <p className="font-medium text-sm">{tx.lawyer.name}</p>
                        {tx.lawyer.verified && <VerifiedBadge role="lawyer" size="xs" />}
                      </div>
                      <p className="text-xs text-slate-500">{tx.lawyer.email}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-sm mt-1 text-slate-400">{tt('Not assigned', 'No asignado')}</p>
                      {(isAgent || isBuyer || ['admin','superadmin'].includes(user?.role)) && (
                        <Dialog open={showAssignLawyer} onOpenChange={(o) => { setShowAssignLawyer(o); if (!o) setInviteMessage(''); }}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="mt-2 gap-1 text-xs" onClick={fetchLawyers} data-testid="assign-lawyer-btn">
                              <UserPlus className="w-3.5 h-3.5" />
                              {['admin','superadmin'].includes(user?.role)
                                ? tt('Assign Lawyer', 'Asignar Abogado')
                                : tt('Invite Lawyer', 'Invitar Abogado')}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md" aria-describedby="assign-lawyer-desc">
                            <DialogHeader><DialogTitle>
                              {['admin','superadmin'].includes(user?.role)
                                ? tt('Assign a Lawyer', 'Asignar un Abogado')
                                : tt('Invite a Lawyer', 'Invitar un Abogado')}
                            </DialogTitle></DialogHeader>
                            <p id="assign-lawyer-desc" className="text-sm text-slate-500">
                              {['admin','superadmin'].includes(user?.role)
                                ? tt('Select a lawyer to handle legal aspects of this transaction.', 'Seleccione un abogado para gestionar los aspectos legales de esta transaccion.')
                                : tt('Send an invitation. The lawyer has 72h to accept or it auto-declines.', 'Envía una invitación. El abogado tiene 72h para aceptar o se rechaza automáticamente.')}
                            </p>
                            {!['admin','superadmin'].includes(user?.role) && (
                              <div className="mt-2">
                                <Label className="text-xs">{tt('Message (optional)', 'Mensaje (opcional)')}</Label>
                                <Textarea
                                  value={inviteMessage}
                                  onChange={(e) => setInviteMessage(e.target.value.slice(0, 500))}
                                  placeholder={tt('e.g. "We need help with due diligence next week."', 'ej. "Necesitamos ayuda con la debida diligencia la próxima semana."')}
                                  className="mt-1 text-sm"
                                  rows={2}
                                  data-testid="invite-message-input"
                                />
                                <p className="text-[10px] text-slate-400 text-right">{inviteMessage.length}/500</p>
                              </div>
                            )}
                            <div className="space-y-2 mt-2 max-h-[50vh] overflow-y-auto">
                              {availableLawyers.length > 0 ? availableLawyers.map(l => (
                                <div key={l.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`lawyer-option-${l.id}`}>
                                  <div>
                                    <div className="flex items-center gap-1.5">
                                      <p className="font-medium text-sm">{l.name || l.email}</p>
                                      {l.verified && <VerifiedBadge role="lawyer" size="xs" />}
                                    </div>
                                    <p className="text-xs text-slate-400">{l.email}</p>
                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                      {l.active_transactions} {tt('active', 'activas')} / {l.completed_transactions} {tt('completed', 'completadas')}
                                    </p>
                                  </div>
                                  <Button
                                    size="sm"
                                    className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]"
                                    onClick={() => (['admin','superadmin'].includes(user?.role) ? assignLawyer(l.id) : inviteLawyer(l.id))}
                                    data-testid={`select-lawyer-${l.id}`}
                                  >
                                    {['admin','superadmin'].includes(user?.role)
                                      ? tt('Assign', 'Asignar')
                                      : tt('Invite', 'Invitar')}
                                  </Button>
                                </div>
                              )) : (
                                <p className="text-sm text-slate-400 text-center py-4">{tt('No lawyers available. Lawyers need to register first.', 'No hay abogados disponibles. Los abogados deben registrarse primero.')}</p>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
